import { WhipError, errorMessage } from './errors';

/** What a 201 tells us, as the server said it. Interpreting it is the caller's. */
export interface WhipPostResult {
	/** The SDP answer, ready for `setRemoteDescription`. */
	answerSdp: string;
	/**
	 * The `Location` header verbatim — normally an absolute url for the session resource, `null` if it was absent.
	 *
	 * Deliberately not resolved here. A relative value would have to be resolved against the request url, and only the
	 * caller knows whether the host it asked serves session routes at all: through the load balancer it does not.
	 */
	location: string | null;
	/**
	 * The transcoder's id for the HLS output, from `X-HLS-Stream-Id`.
	 *
	 * A debugging affordance — it is how the engineer client finds a stream it just started on the viewer side. Production
	 * code should not route on it: nothing guarantees the header, and the HLS layout it refers to is not part of this
	 * protocol.
	 */
	debugHlsStreamId: string | null;
}

/** Where the DELETE got to. Never a throw: see {@link deleteResource}. */
export type WhipDeleteOutcome = { ok: true; status: number } | { ok: false; status: number } | { ok: false; error: string };

/**
 * The url to POST the offer at.
 *
 * A page-relative endpoint is allowed and resolved against the document; outside a browser there is no base, so a
 * relative endpoint there is a mistake worth naming rather than a `TypeError`.
 */
export function buildIngestUrl(endpoint: string, options: { maxVideoShortSide?: number | null } = {}): URL {
	if (!endpoint) throw new WhipError('invalid-options', 'endpoint is empty');

	const base = typeof globalThis.location === 'undefined' ? undefined : globalThis.location.href;
	let url: URL;
	try {
		url = new URL(endpoint, base);
	} catch {
		throw new WhipError('invalid-options', `endpoint is not a url: ${endpoint}`);
	}

	// Honoured the whole way down: the load balancer forwards the query, the gateway re-appends it to the transcoder
	// query, and str0mien1 reads it in max_video_size.rs to cap the ABR ladder — the ladder is locked on the first
	// keyframe, which arrives while WebRTC is still ramping resolution up, so only the publisher can say how large the
	// frames will get. Out of range is a 400, which is why the value is checked before it gets here.
	if (options.maxVideoShortSide) url.searchParams.set('max_video_short_side', String(options.maxVideoShortSide));

	return url;
}

/** `Retry-After` as an instant, from either of the two forms the header allows: delta-seconds or an HTTP date. */
function retryAt(response: Response): Date | undefined {
	const header = response.headers.get('Retry-After');
	if (!header) return undefined;

	const seconds = Number(header);
	if (Number.isFinite(seconds)) return new Date(Date.now() + seconds * 1000);

	const date = Date.parse(header);
	return Number.isNaN(date) ? undefined : new Date(date);
}

async function failureFor(response: Response): Promise<WhipError> {
	// Every failure body on this path is text/plain, and the terse ones are the informative ones.
	const body = await response.text().catch(() => '');
	const detail = body.trim() ? `: ${body.trim()}` : '';
	const status = response.status;

	switch (status) {
		case 400:
			return new WhipError('bad-request', `the ingest endpoint would not parse the offer${detail}`, { status });
		case 401:
			return new WhipError('unauthorized', `the ingest endpoint rejected our identity headers${detail}`, { status });
		case 415:
			return new WhipError('unsupported-content-type', `the ingest endpoint wants application/sdp${detail}`, { status });
		case 422:
			return new WhipError('unprocessable', `the ingest endpoint declined the offer${detail}`, { status });
		case 502:
			return new WhipError('upstream-failed', `the gateway could not reach a transcoder${detail}`, { status });
		case 503:
			return new WhipError('unavailable', `no transcoder capacity${detail}`, { status, retryAt: retryAt(response) });
		default:
			return new WhipError('http-error', `the ingest endpoint returned ${status} ${response.statusText}${detail}`, { status });
	}
}

/**
 * The WHIP handshake's one request: offer up, answer back.
 *
 * Anything other than 201 becomes a `WhipError` naming the reason, and a `fetch` that rejects outright becomes
 * `network-error` — in a browser that is where a CORS refusal surfaces, indistinguishably from the host being down.
 */
export async function postOffer(params: {
	url: URL;
	sdp: string;
	headers: Record<string, string>;
	fetchImpl?: typeof fetch;
}): Promise<WhipPostResult> {
	const { url } = params;
	const doFetch = params.fetchImpl ?? fetch;
	let response: Response;
	try {
		response = await doFetch(url, {
			method: 'POST',
			headers: { ...params.headers, 'Content-Type': 'application/sdp' },
			body: params.sdp,
		});
	} catch (error) {
		throw new WhipError('network-error', `could not reach ${url.origin}: ${errorMessage(error)} (host down, TLS, or CORS)`, { cause: error });
	}

	if (response.status !== 201) throw await failureFor(response);

	const answerSdp = await response.text();

	return {
		answerSdp,
		location: response.headers.get('Location'),
		debugHlsStreamId: response.headers.get('X-HLS-Stream-Id'),
	};
}

/**
 * Ends the session server-side. Resolves whatever happened.
 *
 * A teardown that throws gives the caller nothing to do with it: the peer connection is closing regardless, and every
 * interesting outcome — 404 already gone, 410 pod gone — means the resource is not there any more, which is the goal. The
 * outcome comes back for the log.
 *
 * `keepalive` is what makes this usable from `beforeunload`, where a normal fetch is abandoned with the page.
 */
export async function deleteResource(resourceUrl: string, fetchImpl?: typeof fetch): Promise<WhipDeleteOutcome> {
	const doFetch = fetchImpl ?? fetch;
	try {
		const response = await doFetch(resourceUrl, { method: 'DELETE', keepalive: true });
		// 200 is the success here, not 204.
		return response.ok ? { ok: true, status: response.status } : { ok: false, status: response.status };
	} catch (error) {
		return { ok: false, error: errorMessage(error) };
	}
}
